import { useEffect, useState } from "react";
import { inputClass, labelClass, primaryButtonClass } from "@/components/base/fieldStyles";
import { categoryBadge } from "@/utils/categories";

interface CategoryManagerProps {
  categories: string[];
  counts: Record<string, number>;
  onSave: (categories: string[]) => void;
}

// Gestión de categorías del catálogo: crear, renombrar y borrar. Se guardan en
// la base (app_settings.categories). Las que vienen del PDF se siguen creando
// solas; aquí quedan editables por si acaso.
const CategoryManager = ({ categories, counts, onSave }: CategoryManagerProps) => {
  const [list, setList] = useState<string[]>(categories);
  const [draft, setDraft] = useState("");
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    setList(categories);
  }, [categories]);

  const add = () => {
    const name = draft.trim();
    if (!name) return;
    if (list.some((c) => c.toLowerCase() === name.toLowerCase())) {
      setFeedback("Esa categoría ya existe.");
      return;
    }
    setList([...list, name]);
    setDraft("");
    setFeedback("");
  };

  const rename = (idx: number, value: string) =>
    setList((prev) => prev.map((c, i) => (i === idx ? value : c)));
  const remove = (idx: number) => setList((prev) => prev.filter((_, i) => i !== idx));

  const save = () => {
    const clean = list.map((c) => c.trim()).filter(Boolean);
    if (clean.length === 0) {
      setFeedback("Debe quedar al menos una categoría.");
      return;
    }
    onSave(clean);
    setFeedback("Categorías guardadas. Ya están disponibles en el catálogo.");
  };

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-lg bg-accent-500/20 text-accent-200 flex items-center justify-center shrink-0">
          <i className="ri-price-tag-3-line text-lg" />
        </span>
        <div>
          <h3 className="editable-title text-base font-bold">Categorías del catálogo</h3>
          <p className="text-[11px] text-foreground-600">
            Crea, renombra o borra categorías. Se guardan en la base y aparecen en el catálogo, el
            inventario y los filtros.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {list.map((cat, idx) => {
          const count = counts[cat] ?? 0;
          return (
            <div key={`${cat}-${idx}`} className="flex items-center gap-2">
              <span
                className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full ${categoryBadge(cat)}`}
              >
                {cat}
              </span>
              <input
                value={cat}
                onChange={(e) => rename(idx, e.target.value)}
                className={`${inputClass} flex-1`}
                aria-label="Nombre de la categoría"
              />
              <span className="text-[10px] text-foreground-500 w-20 text-right shrink-0">
                {count} producto{count === 1 ? "" : "s"}
              </span>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                aria-label="Borrar categoría"
                title="Borrar categoría"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-background-300/25 flex flex-col sm:flex-row sm:items-end gap-2">
        <div className="flex-1">
          <label className={labelClass}>Nueva categoría</label>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
            placeholder="Ej: Mascotas, Electrónica..."
            className={inputClass}
          />
        </div>
        <button
          type="button"
          onClick={add}
          className="px-3.5 py-2 rounded-md border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5"
        >
          <i className="ri-add-line" /> Agregar
        </button>
        <button type="button" onClick={save} className={primaryButtonClass}>
          <i className="ri-save-3-line" /> Guardar categorías
        </button>
      </div>

      <p className="text-[10px] text-foreground-500 mt-3 flex items-start gap-1.5">
        <i className="ri-information-line mt-0.5" />
        Al renombrar o borrar, los productos que ya tenían la categoría anterior conservan su
        etiqueta; reasígnalos editando el producto si quieres moverlos.
      </p>

      {feedback && (
        <div className="mt-3 rounded-md bg-background-100/60 border border-accent-500/25 px-3 py-2 flex items-center gap-2">
          <i className="ri-information-line text-accent-300" />
          <p className="text-[11px] text-foreground-700">{feedback}</p>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;